// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  withStyles,
  Typography,
  Grid,
  FormHelperText,
  Table,
  TableCell,
  TableBody,
  TableRow,
  TableHead,
  Button,
} from '@material-ui/core';
import WarningIcon from "@material-ui/icons/Warning";
import { Clear } from '@material-ui/icons';
import { forEach, get, map, some } from 'lodash';
import queryString from 'query-string';
import { shape, string, func } from 'prop-types';
import AddCircleOutline from '@material-ui/icons/AddCircleOutline';

import { getI18n, getI18nFormattedMessage } from '../../../../utils/intl';
import {
  AlertMessages,
  ErrorContainer,
  FormTextField,
  FormActionButtons,
  RadioGroupSection,
  FormSelect,
} from '../../../../components';
import { updateAuthScheme, saveAuthScheme, setAuthSettings } from '../../../../actions/authenticationSettings';
import {
  AUTH_SETTINGS_LDAP_AUTH_TYPE_DATA,
  AUTH_SETTINGS_SAML_ROLE_TYPE_DATA,
  SAML_MISMATCH_DATA,
  IDP_TYPE,
  PORTAL_TYPE,
  SINGLE_ORG_TYPE,
  MULTIPLE_ORG_TYPE,
  SAML_OLD,
  SAML,
  LDAP,
  JWT,
} from '../label';
import { hasError } from '../../../../utils';
import Actions from '../Actions';
import styles from '../styles';

export const AttributeMapping = (props) => {
  const {
    classes,
    errors,
    authDetails,
    uuid,
    notifyMessages,
    tabValue,
    onCancelDetails,
  } = props;

  const intl = getI18n(useIntl());
  const queryParam = queryString.parse(props.location.search);
  const authParamType = get(queryParam, 'authType');
  const qParam = authParamType ? `?authType=${queryParam.authType}` : '';
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [email, setEmail] = useState('');
  const [isEmailError, setIsEmailError] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [isFirstNameError, setIsFirstNameError] = useState(false);
  const [lastName, setLastName] = useState('');
  const [isLastNameError, setIsLastNameErrror] = useState(false);
  const [login, setLogin] = useState('');
  const [isLoginError, setIsLoginError] = useState('');
  const [org, setOrg] = useState('');
  const [isOrgError, setIsOrgError] = useState(false);
  const [role, setRole] = useState('');
  const [isRoleError, setIsRoleError] = useState(false);
  const [isPortalAdminError, setIsPortalAdminError] = useState(false);
  const [authType, setAuthType] = useState(IDP_TYPE);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [roleType, setRoleType] = useState(SINGLE_ORG_TYPE);
  const [orgRoleAttribute, setOrgRoleAttribute] = useState('');
  const [isOrgRoleAttributeError, setIsOrgRoleAttributeError] = useState(false);
  const [mappingFormat, setMappingFormat] = useState('');
  const [isMappingFormatError, setIsMappingFormatError] = useState(false);
  const [delimiter, setDelimiter] = useState('');
  const [isDelimiterError, setIsDelimiterError] = useState(false);
  const [handlingMismatch, setHandlingMismatch] = useState('FAIL');
  const [roleMap, setRoleMap] = useState({});
  const rolesList = { portaladministrators: 'label.auth.portal.administrator',
                      apiowners: 'label.auth.api.owner',
                      devorgadministrators: 'label.auth.org.administrator',
                      orgpublishers: 'label.auth.org.publisher',
                      developers: 'label.auth.developer',
                    };

  useEffect(() => {
    notifyMessages('', '');
    if(tabValue === 'attribute-mapping-tab') {
      setEmail(get(authDetails, 'AttributeMappings.EMAIL_ATTRIBUTE'));
      setFirstName(get(authDetails, 'AttributeMappings.FIRST_NAME_ATTRIBUTE'));
      setLastName(get(authDetails, 'AttributeMappings.LAST_NAME_ATTRIBUTE'));
      setOrg(get(authDetails, 'AttributeMappings.ORGANIZATION_ATTRIBUTE'));
      setRole(get(authDetails, 'AttributeMappings.ROLE_ATTRIBUTE'));
      setLogin(get(authDetails, 'AttributeMappings.LOGIN_ATTRIBUTE'));

      if (get(authDetails, 'authznType') === 'PORTAL') {
        setAuthType(PORTAL_TYPE);
      } else if (get(authDetails, 'authznType') === 'IDP') {
        setAuthType(IDP_TYPE);
      }
      if(!uuid && authParamType === SAML) {
        setAuthType(IDP_TYPE);
      }

      if(authParamType === SAML || authParamType === JWT) {
        const rType = get(authDetails, 'ConfigurationParams.orgRoleMappingType') === 'multiple'
          ? MULTIPLE_ORG_TYPE : SINGLE_ORG_TYPE;
        setRoleType(rType);
        setDelimiter(get(authDetails, 'ConfigurationParams.multiOrgRoleDelimiter'));
        setOrgRoleAttribute(get(authDetails, 'AttributeMappings.MULTI_ORG_ROLE_MAPPING_ATTRIBUTE'));
        if(get(authDetails, 'ConfigurationParams.multiOrgRoleFormatMismatchAction'))
        setHandlingMismatch(get(authDetails, 'ConfigurationParams.multiOrgRoleFormatMismatchAction'));
        setMappingFormat(get(authDetails, 'ConfigurationParams.multiOrgRoleFormat'))
      }
    }
  }, [authDetails, tabValue]);

  useEffect(() => {
    const roleMappings = get(authDetails, 'RoleMappings');
    if(Object.keys(roleMappings).length > 0) {
      setRoleMap({});
      forEach(roleMappings, (value, key) => {
        setRoleMap(roleMap => {
          return {
            ...roleMap,
            [value] :
              roleMap[value] ? [
                ...roleMap[value],
                key,
              ] : [key],
          }
        });
      });
    }
  }, [authDetails]);

  const handleChange = (fieldName, value, idx) => {
    switch (fieldName) {
      case 'firstName': {
        const firstNameError = hasError(value, true);
        setFirstName(value);
        setIsFirstNameError(firstNameError);
        break;
      }
      case 'lastName': {
        const lastNameError = hasError(value, true);
        setLastName(value);
        setIsLastNameErrror(lastNameError);
        break;
      }
      case 'email': {
        const emailError = hasError(value, true);
        setEmail(value);
        setIsEmailError(emailError);
        break;
      }
      case 'login': {
        if((authType === IDP_TYPE && authParamType === SAML_OLD) ||
          authParamType === SAML || authParamType === JWT) {
          const loginError = hasError(value, true);
          setLogin(value);
          setIsLoginError(loginError);
        }
        break;
      }
      case 'orgName': {
        if(authType === IDP_TYPE || (roleType === SINGLE_ORG_TYPE &&
          (authParamType === SAML || authParamType === JWT))) {
          const orgError = hasError(value, true);
          setOrg(value);
          setIsOrgError(orgError);
        }
        break;
      }
      case 'role': {
        if(authType === IDP_TYPE || (roleType === SINGLE_ORG_TYPE &&
          (authParamType === SAML || authParamType === JWT))) {
          const roleError = hasError(value, true);
          setRole(value);
          setIsRoleError(roleError);
        }
        break;
      }
      case 'orgRoleAttribute': {
        if(roleType === MULTIPLE_ORG_TYPE) {
          const orgRoleAttributeError = hasError(value, true);
          setOrgRoleAttribute(value);
          setIsOrgRoleAttributeError(orgRoleAttributeError);
        }
        break;
      }
      case 'mappingFormat': {
        if(roleType === MULTIPLE_ORG_TYPE) {
          const mappingFormatError = hasError(value, true);
          setMappingFormat(value);
          setIsMappingFormatError(mappingFormatError);
        }
        break;
      }
      case 'delimiter': {
        if(roleType === MULTIPLE_ORG_TYPE) {
          const delimiterError = hasError(value, true);
          setDelimiter(value);
          setIsDelimiterError(delimiterError);
        }
        break;
      }
      case 'portaladministrators':
      case 'apiowners':
      case 'devorgadministrators':
      case 'orgpublishers':
      case 'developers': {
        let roleList = roleMap[fieldName];
        if(!roleList || roleList.length === 0) {
          roleList = [value];
        } else {
          roleList[idx] = value;
        }
        setRoleMap({
          ...roleMap,
          [fieldName] : roleList,
        });
        break;
      }
      case 'handlingMismatch':
        setHandlingMismatch(value);
        break;
    }
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  }

  const onChangeAuthType = (e) => {
    setAuthType(e.target.value);
    if(e.target.value === SINGLE_ORG_TYPE){
      setRoleType(SINGLE_ORG_TYPE);
    }
  }
  const addRoleField = (roleName) => {
    const roleList = roleMap[roleName];
    roleList.push('');
    setRoleMap({
      ...roleMap,
      [roleName] : roleList,
    });
  }
  const onChangeRoleType = (e) => {
    setRoleType(e.target.value);
  }

  const assistText = authParamType === LDAP && uuid ? (<span>
    <WarningIcon className={classes.warningIcon} />
    {getI18nFormattedMessage('label.auth.ldap.assist.text')}
    </span>) : '';

  const onNext = () => {
    const emailError = hasError(email, true);
    const firstNameError = hasError(firstName, true);
    const lastNameError = hasError(lastName, true);
    let orgError = false;
    let roleError = false;
    let portalAdminError = false;
    let loginError = false;
    let delimiterError = false;
    let mappingFormatError = false;
    let orgRoleAttributeError = false;
    if((authType === IDP_TYPE && (authParamType !== SAML && authParamType !== JWT))
      || (authType === IDP_TYPE &&
      roleType === SINGLE_ORG_TYPE && (authParamType === SAML || authParamType === JWT))) {
      orgError = hasError(org, true);
      roleError = hasError(role, true);
    }
    if(authType === IDP_TYPE) {
      const adminRoleList = roleMap['portaladministrators'] || [];
      portalAdminError = adminRoleList.every(role => !role);
      if(authParamType === SAML_OLD) {
        loginError = hasError(login, true);
      }
    }
    if(authParamType === SAML || authParamType === JWT) {
      loginError = hasError(login, true);
    }
    if(roleType === MULTIPLE_ORG_TYPE) {
      orgRoleAttributeError = hasError(orgRoleAttribute, true);
      mappingFormatError = hasError(mappingFormat, true);
      delimiterError = hasError(delimiter, true);
    }
    if (some([
      emailError, firstNameError, lastNameError, orgError, delimiterError,
      roleError, loginError, orgRoleAttributeError, mappingFormatError, portalAdminError,
    ])) {
      setIsEmailError(emailError);
      setIsFirstNameError(firstNameError);
      setIsLastNameErrror(lastNameError);
      setIsOrgError(orgError)
      setIsRoleError(roleError);
      setIsLoginError(loginError);
      setIsOrgRoleAttributeError(orgRoleAttributeError);
      setIsMappingFormatError(mappingFormatError);
      setIsDelimiterError(delimiterError);
      setIsPortalAdminError(portalAdminError);
    } else {
      setNotificationStatus('');
      setNotificationMessage('');
      let roleMappaings = {};
      Object.keys(roleMap).forEach(key => {
        const roleList = roleMap[key];
        forEach(roleList, (value) => {
          if(value) {
            roleMappaings[value] = key;
          }
        });
      });

      if(authType === PORTAL_TYPE) {
        roleMappaings = {};
      }
      let aMappings = {
        EMAIL_ATTRIBUTE: email,
        FIRST_NAME_ATTRIBUTE: firstName,
        LAST_NAME_ATTRIBUTE: lastName,
      };
      aMappings = ((authType === IDP_TYPE && (authParamType !== SAML && authParamType !== JWT))
      || (authType === IDP_TYPE &&
      roleType === SINGLE_ORG_TYPE && (authParamType === SAML || authParamType === JWT))) ?
        { ...aMappings, ...{
          ORGANIZATION_ATTRIBUTE: org,
          ROLE_ATTRIBUTE: role,
        },
      }: aMappings;
      aMappings = authParamType === SAML_OLD || authParamType === SAML
      || authParamType === JWT ?
        { ...aMappings, ...{ LOGIN_ATTRIBUTE : login } } : aMappings ;
      aMappings = (authParamType === SAML || authParamType === JWT)
      && roleType === MULTIPLE_ORG_TYPE ?
        { ...aMappings, ...{ MULTI_ORG_ROLE_MAPPING_ATTRIBUTE : orgRoleAttribute } } : aMappings ;
      const type = roleType === MULTIPLE_ORG_TYPE ? 'multiple' : 'single';
      let configParams = authDetails.ConfigurationParams;
      if(authParamType === SAML) {
        if (roleType === MULTIPLE_ORG_TYPE) {
          configParams = { ...authDetails.ConfigurationParams,
            ...{ multiOrgRoleDelimiter : delimiter,
            multiOrgRoleFormat: mappingFormat, multiOrgRoleFormatMismatchAction: handlingMismatch,
            orgRoleMappingType: type,
            } };
        }
        if (roleType === SINGLE_ORG_TYPE) {
            delete authDetails.ConfigurationParams.multiOrgRoleDelimiter;
            delete authDetails.ConfigurationParams.multiOrgRoleFormat;
            delete authDetails.ConfigurationParams.multiOrgRoleFormatMismatchAction;
            configParams = { ...authDetails.ConfigurationParams, orgRoleMappingType: type };
        }
      }
      if(authParamType === JWT) {
        configParams = {
          discoveryEndPoint: get(authDetails, 'ConfigurationParams.discoveryEndPoint'),
          audienceId: get(authDetails, 'ConfigurationParams.audienceId'),
        };
        if (roleType === MULTIPLE_ORG_TYPE) {
          configParams = { ...configParams,
            ...{ multiOrgRoleDelimiter : delimiter,
            multiOrgRoleFormat: mappingFormat, multiOrgRoleFormatMismatchAction: handlingMismatch,
            orgRoleMappingType: type,
            } };
        }
        if (roleType === SINGLE_ORG_TYPE) {
            delete authDetails.ConfigurationParams.multiOrgRoleDelimiter;
            delete authDetails.ConfigurationParams.multiOrgRoleFormat;
            delete authDetails.ConfigurationParams.multiOrgRoleFormatMismatchAction;
            configParams = { ...configParams, orgRoleMappingType: type };
        }
      }
      const data = { ...authDetails, ...{
        RoleMappings: roleMappaings,
        AttributeMappings: aMappings,
        authznType: authType === IDP_TYPE ? 'IDP' : 'PORTAL',
        ConfigurationParams: configParams,
      } };
      if (authType === PORTAL_TYPE) {
        delete data.RoleMappings;
      }
      if (uuid) {
        props.updateAuthScheme(uuid, data);
      } else {
        props.setAuthSettings(data);
        props.saveAuthScheme(uuid, data);
        props.push(`/publish/settings/authentication/add/attribute-mapping${qParam}`);
      }
    }
  }

  const onCancel = () => {
    onCancelDetails(isUnsavedChanges);
  }
  const getRolesList = roleName => roleMap[roleName] && roleMap[roleName].length > 0 ? roleMap[roleName] : [''];

  return(
    <div id="auth-settings-attribute-mapping-container" className={classes.pageContainer}>
      {notificationMessage &&
        <AlertMessages
          id="auth-settings-attribute-mapping-notifications"
          message={notificationMessage}
          variant={notificationStatus}
          onClose={() => {
            setNotificationMessage('');
            setNotificationStatus('');
          }}
        />
      }
      {errors.length > 0 &&
        <ErrorContainer errors={errors} />
      }
      <Grid container>
        <Grid md={10} item>
          <Typography variant="h1" className={classes.pageTitle}>
            {intl.getI18nMessage('label.auth.attribute.mapping')}
          </Typography>
        </Grid>
        <Grid md={2} item>
          <div className={classes.deleteContainer}>
            <Actions
              {...props}
              uuid={uuid}
              notifyMessages={notifyMessages}
              disabled={get(authDetails, 'defaultConfig') }
            />
          </div>
        </Grid>
      </Grid>
      <FormHelperText>
        <div className={classes.helperBodyText}>
          {intl.getI18nMessage('label.auth.attribute.mapping.help.text')}
        </div>
      </FormHelperText>
      <Grid container spacing={3} className={classes.gridContainer}>
        <Grid md={12} item>
          {authParamType !== SAML_OLD &&
            <>
              <Typography variant="h4">
                {getI18nFormattedMessage('label.auth.authorize.type')}
              </Typography>
              <RadioGroupSection
                selected={authType}
                onChange={onChangeAuthType}
                data={AUTH_SETTINGS_LDAP_AUTH_TYPE_DATA}
                id="management-radio-group"
              />
            </>
          }
          {(authParamType === SAML || authParamType === JWT)
            && authType === IDP_TYPE &&
            <>
              <Typography variant="h4">
                {getI18nFormattedMessage('label.authorize.role.type')}
              </Typography>
              <RadioGroupSection
                selected={roleType}
                onChange={onChangeRoleType}
                data={AUTH_SETTINGS_SAML_ROLE_TYPE_DATA}
                id="role-radio-group"
              />
            </>
          }
          <Typography variant="h4">
            {getI18nFormattedMessage('label.auth.basic.attribute.name.mapping')}
          </Typography>
          <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{intl.getI18nMessage('label.auth.portal')}</TableCell>
                  <TableCell>{intl.getI18nMessage('label.auth.idp.attribute.name')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell className={classes.cellText}>{intl.getI18nMessage('label.email')}</TableCell>
                  <TableCell>
                    <FormTextField
                      id={'auth-settings-am-email'}
                      fieldContainerClass={classes.attributeFieldContainer}
                      textFieldClass={classes.attributeTextFieldContainer}
                      name={''}
                      value={email}
                      error={isEmailError}
                      handleChange={(value) => handleChange('email', value)}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.cellText}>{intl.getI18nMessage('label.auth.first.name')}</TableCell>
                  <TableCell>
                    <FormTextField
                      id={'auth-settings-am-first-name'}
                      fieldContainerClass={classes.attributeFieldContainer}
                      textFieldClass={classes.attributeTextFieldContainer}
                      name={''}
                      value={firstName}
                      error={isFirstNameError}
                      handleChange={(value) => handleChange('firstName', value)}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.cellText}>{intl.getI18nMessage('label.auth.last.name')}</TableCell>
                  <TableCell>
                    <FormTextField
                      id={'auth-settings-am-last-name'}
                      fieldContainerClass={classes.attributeFieldContainer}
                      textFieldClass={classes.attributeTextFieldContainer}
                      name={''}
                      value={lastName}
                      error={isLastNameError}
                      handleChange={(value) => handleChange('lastName', value)}
                    />
                  </TableCell>
                </TableRow>
                {(authParamType === SAML || authParamType === JWT) &&
                  <TableRow>
                    <TableCell className={classes.cellText}>{intl.getI18nMessage('label.login')}</TableCell>
                    <TableCell>
                      <FormTextField
                        id={'auth-settings-am-login'}
                        fieldContainerClass={classes.attributeFieldContainer}
                        textFieldClass={classes.attributeTextFieldContainer}
                        name={''}
                        value={login}
                        error={isLoginError}
                        handleChange={(value) => handleChange('login', value)}
                      />
                    </TableCell>
                  </TableRow>
                }
                {authType === IDP_TYPE &&
                  <>
                    {authParamType === SAML_OLD &&
                      <TableRow>
                        <TableCell className={classes.cellText}>{intl.getI18nMessage('label.login')}</TableCell>
                        <TableCell>
                          <FormTextField
                            id={'auth-settings-am-login'}
                            fieldContainerClass={classes.attributeFieldContainer}
                            textFieldClass={classes.attributeTextFieldContainer}
                            name={''}
                            value={login}
                            error={isLoginError}
                            handleChange={(value) => handleChange('login', value)}
                          />
                        </TableCell>
                      </TableRow>
                    }
                    <>
                      {((roleType === SINGLE_ORG_TYPE && (authParamType === SAML ||
                        authParamType === JWT)) || authParamType === SAML_OLD ||
                        authParamType === LDAP) &&
                        <>
                          <TableRow>
                            <TableCell className={classes.cellText}>{intl.getI18nMessage('label.organization')}</TableCell>
                            <TableCell>
                              <FormTextField
                                id={'auth-settings-am-organization'}
                                fieldContainerClass={classes.attributeFieldContainer}
                                textFieldClass={classes.attributeTextFieldContainer}
                                name={''}
                                value={org}
                                error={isOrgError}
                                handleChange={(value) => handleChange('orgName', value)}
                              />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.cellText}>{intl.getI18nMessage('label.role')}</TableCell>
                            <TableCell>
                              <FormTextField
                                id={'auth-settings-am-role'}
                                fieldContainerClass={classes.attributeFieldContainer}
                                textFieldClass={classes.attributeTextFieldContainer}
                                name={''}
                                value={role}
                                error={isRoleError}
                                handleChange={(value) => handleChange('role', value)}
                              />
                            </TableCell>
                          </TableRow>
                        </>
                      }
                    </>
                  </>
                }
              </TableBody>
          </Table>
          {authType === IDP_TYPE &&
            <>
              <Typography variant="h4">
                {getI18nFormattedMessage('label.auth.portal.roles.mapping')}
              </Typography>
              <FormHelperText>
                <div className={classes.helperBodyText}>
                  {intl.getI18nMessage('label.auth.portal.roles.mapping.help.text')}
                </div>
              </FormHelperText>
              <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.cellText}>{intl.getI18nMessage('label.auth.portal')}</TableCell>
                      <TableCell>{intl.getI18nMessage('label.auth.idp.attribute.name')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(rolesList).map(roleName =>
                      <TableRow key={roleName}>
                        <TableCell className={classes.roleMappingPortalRoleCell}>
                          {intl.getI18nMessage(rolesList[roleName])}
                        </TableCell>
                        <TableCell className={classes.roleInnerTable}>
                          <Table>
                            <TableBody className={classes.roleInnerTableTbody}>
                              {map(getRolesList(roleName) , (roleValue, idx) =>
                                <TableRow>
                                  <TableCell className={classes.roleCell}>
                                    <FormTextField
                                      id={`auth-settings-am-${roleName}`}
                                      fieldContainerClass={classes.attributeFieldContainer}
                                      textFieldClass={classes.attributeTextFieldContainer}
                                      name={''}
                                      value={roleValue}
                                      error={roleName === "portaladministrators" && isPortalAdminError}
                                      handleChange={(value) => handleChange(roleName, value, idx)}
                                      suffixIcon={roleValue &&
                                        <Clear className={classes.clearIcon} color="primary" onClick={() => handleChange(roleName, '', idx)}/>}
                                    />
                                    {roleName === "portaladministrators" && idx === 0 &&
                                      <FormHelperText error={isPortalAdminError}>
                                        <div className={classes.helperBodyText}>
                                          {intl.getI18nMessage('label.auth.role.mapping.field.help.text')}
                                        </div>
                                      </FormHelperText>
                                    }
                                  </TableCell>
                                  <TableCell className={classes.plusButtonCell}>
                                    {roleMap[roleName] && idx === roleMap[roleName].length - 1 &&
                                      <Button
                                          onClick={() => addRoleField(roleName)}
                                          className={classes.plusButton}
                                          disabled={roleMap[roleName].some(role => !role)}
                                      >
                                        <AddCircleOutline
                                            className={roleMap[roleName].some(role => !role) ?
                                              classes.addRoleButtonDisabled : classes.addRoleButton}
                                            disabled={roleMap[roleName].some(role => !role)}
                                        />
                                      </Button>
                                    }
                                  </TableCell>
                                </TableRow>)}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
              </Table>
            </>
          }
          {roleType === MULTIPLE_ORG_TYPE &&
            <>
              <Typography variant="h4">
                {getI18nFormattedMessage('label.auth.multiple.roles.title')}
              </Typography>
              <FormHelperText>
                <div className={classes.helperBodyText}>
                  {intl.getI18nMessage('label.auth.multiple.roles.title.help.text')}
                </div>
              </FormHelperText>
              <FormTextField
                id={'auth-settings-org-role-attribute'}
                name={intl.getI18nMessage('label.auth.org.role.attribute')}
                value={orgRoleAttribute}
                error={isOrgRoleAttributeError}
                handleChange={(value) => handleChange('orgRoleAttribute', value)}
                helperText={intl.getI18nMessage('label.auth.org.role.attribute.help.text')}
              />
              <FormTextField
                id={'auth-settings-org-mapping-format'}
                name={intl.getI18nMessage('label.auth.org.mapping.format')}
                value={mappingFormat}
                error={isMappingFormatError}
                handleChange={(value) => handleChange('mappingFormat', value)}
                helperText={intl.getI18nMessage('label.auth.org.mapping.format.help.text')}
                placeholder={'${organization}_${role}'}
              />
              <FormTextField
                id={'auth-settings-org-delimiter'}
                name={intl.getI18nMessage('label.auth.org.delimiter')}
                value={delimiter}
                error={isDelimiterError}
                handleChange={(value) => handleChange('delimiter', value)}
                helperText={''}
              />
              <FormSelect
                id={'auth-settings-org-handling-mismatch'}
                name={getI18nFormattedMessage('label.auth.org.handling.mismatch')}
                value={handlingMismatch}
                data={SAML_MISMATCH_DATA}
                handleChange={(value) => handleChange('handlingMismatch', value)}
                helperText={intl.getI18nMessage('label.auth.org.handling.mismatch.help.text')}
                noNativeSelect
                noNoneOption={true}
              />
            </>
          }
        </Grid>
      </Grid>
      <FormActionButtons
        onNextClick={onNext}
        nextText={getI18nFormattedMessage('label.save.button')}
        onCancelClick={onCancel}
        id="auth-settings-attribute-mapping-buttons"
        assistText={assistText}
      />
    </div>
  )
}

AttributeMapping.propTypes = {
    classes: shape({}),
    errors: shape({}),
    authDetails: shape({}),
    location: shape({}),
    uuid: string,
    notifyMessages: func,
    setStateTabs: func,
    saveAuthScheme: func,
    updateAuthScheme: func,
    tabValue: string,
    onCancelDetails: func,
    push: func,
    setAuthSettings: func,
};

const mapStateToProps = () => ({
});

const mapDispatchToProps = {
  setAuthSettings,
  updateAuthScheme,
  saveAuthScheme,
};

export default compose(
withStyles(styles),
connect(mapStateToProps, mapDispatchToProps),
)(AttributeMapping);
